import { useEffect, useRef, useState } from 'react';

import { Navigation03Icon, PencilEdit01Icon } from '@hugeicons/core-free-icons';
import { EMAIL_TEMPLATE_TYPES } from 'constants/email-config';
import { FilesFolders } from 'constants/files-folders-enum';
import { useApiUploadActions } from 'hooks/upload-actions';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { EmailTemplateStep } from 'components/EmailTemplateStep/EmailTemplateStep';
import { Button, FilePicker, InputGroup, MultipleChoice, Select } from 'components/FormComponents';

const findFormId = (forms, formId) => forms.find(form => form.id == formId)?.id ?? undefined;

export const NewRefereeModal = ({
  visible,
  candidateForms = [],
  refereeForms = [],
  selectedCandidateForm = null,
  selectedRefereeForm = null,
  documents = [],
  selectedDocumentId = null,
  brand = {},
  onSelect = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const [askCandidateTemplateId, setAskCandidateTemplateId] = useState(null);
  const prevVisibleRef = useRef(false);

  const [upload, setUpload] = useState();
  const [showNewUploadModal, setShowNewUploadModal] = useState(false);

  const { signUpload, createUpload, deleteUpload } = useApiUploadActions();

  const candidateFormIdValue = watch('candidate_form_id');
  const refereeDocumentIdValue = watch('referee_document_id');
  const refereeFormIdValue = watch('referee_form_id');
  const inputType = watch('input_type');

  useEffect(() => {
    if (!visible) {
      if (prevVisibleRef.current) {
        setAskCandidateTemplateId(null);
        reset();
      }
      prevVisibleRef.current = false;
      return;
    }

    if (!prevVisibleRef.current) {
      reset({
        candidate_form_id: findFormId(candidateForms, selectedCandidateForm),
        referee_form_id: findFormId(refereeForms, selectedRefereeForm),
        referee_document_id: selectedDocumentId ?? undefined,
        input_type: 'enter_details',
      });
    }
    prevVisibleRef.current = true;
  }, [visible, reset, selectedCandidateForm, selectedRefereeForm, selectedDocumentId]);

  useEffect(() => {
    if (!visible) return;
    if (!getValues('candidate_form_id')) {
      const candidateFormId = findFormId(candidateForms, selectedCandidateForm);
      if (candidateFormId) setValue('candidate_form_id', candidateFormId);
    }
    if (!getValues('referee_form_id')) {
      const refereeFormId = findFormId(refereeForms, selectedRefereeForm);
      if (refereeFormId) setValue('referee_form_id', refereeFormId);
    }
  }, [
    visible,
    candidateForms.length,
    refereeForms.length,
    selectedCandidateForm,
    selectedRefereeForm,
  ]);

  useEffect(() => {
    if (inputType !== 'ask_candidate') {
      setAskCandidateTemplateId(null);
    }
  }, [inputType]);

  const formRequired = (forms, message) => ({
    validate: value => forms.some(form => form.id == value) || message,
  });

  const onSubmit = data => {
    if (data.input_type === 'ask_candidate') {
      onSelect({
        ...data,
        template_id: askCandidateTemplateId,
      });
      return;
    }
    onSelect(data);
  };

  const setUploadedDocument = () => {
    if (upload?.id) {
      setValue('referee_document_id', upload.id);
      setShowNewUploadModal(false);
    }
  };

  const formBody = (
    <>
      <div className="card">
        <div className="card">
          <InputGroup title="Candidate Form">
            <Select
              name="candidate_form_id"
              register={register}
              validators={formRequired(candidateForms, 'Candidate form is required')}
              value={candidateFormIdValue}
              error={errors?.candidate_form_id?.message}>
              {candidateForms.map(candidateForm => (
                <Select.Item value={candidateForm.id} key={candidateForm.id}>
                  {candidateForm.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>

          <InputGroup title="Referee Form">
            <Select
              name="referee_form_id"
              register={register}
              validators={formRequired(refereeForms, 'Referee form is required')}
              value={refereeFormIdValue}
              error={errors?.referee_form_id?.message}>
              {refereeForms.map(refereeForm => (
                <Select.Item value={refereeForm.id} key={refereeForm.id}>
                  {refereeForm.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>

          <InputGroup title="Documents" className="u-margin-bottom--0">
            <Select
              name="referee_document_id"
              placeholder={t('common.document')}
              className="u-margin-bottom--0"
              register={register}
              value={refereeDocumentIdValue}>
              {upload && !documents.some(document => document.id === upload.id) && (
                <Select.Item value={upload.id}>{upload.file_name}</Select.Item>
              )}
              {documents.map(document => (
                <Select.Item key={document.id} value={document.id}>
                  {document.file_name}
                </Select.Item>
              ))}
            </Select>
            <a className="u-link dark t-small" onClick={() => setShowNewUploadModal(true)}>
              {t('common.uploadNewDocument')}
            </a>
          </InputGroup>
        </div>

        <Controller
          name="input_type"
          defaultValue="enter_details"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultipleChoice
              name="input_type"
              className="u-margin-top"
              value={value}
              options={{
                options: [
                  {
                    name: 'Enter Referee Details Yourself',
                    description: "Manually enter the referee's details into Checkmate.",
                    value: 'enter_details',
                    icon: PencilEdit01Icon,
                  },
                  {
                    name: 'Ask Candidate to Provide Details',
                    description:
                      "We'll notify the candidate to add their referee's information directly.",
                    value: 'ask_candidate',
                    icon: Navigation03Icon,
                  },
                ],
              }}
              onChange={onChange}
              radioButtonStyle
              prefixIcon
            />
          )}
        />
      </div>

      {inputType === 'ask_candidate' && (
        <div className="mt-6">
          <EmailTemplateStep
            brand={brand}
            category="candidate"
            standardName={t('check.askCandidateNewRefereeEmail.emailStandardName')}
            standardDescription={t('check.askCandidateNewRefereeEmail.emailStandardDescription')}
            previewStandardMessage={t('check.askCandidateNewRefereeEmail.emailPreviewStandard')}
            type={EMAIL_TEMPLATE_TYPES.NEW_REFEREE_NOMINATION}
            hideFooter
            onSelectedTemplateIdChange={setAskCandidateTemplateId}
          />
        </div>
      )}
    </>
  );

  const formActions = compactTop => (
    <div
      className={
        compactTop
          ? 'd-flex justify-content-between'
          : 'd-flex justify-content-between u-margin-top--large'
      }>
      <Button className="u-width-100" onClick={onClose} type="secondary">
        Cancel
      </Button>
      <Button
        className="u-width-100 u-margin-left--large"
        submit
        dataTestId="new-referee-submit-button">
        Add Referee
      </Button>
    </div>
  );

  return (
    <>
      <AnimatedModal
        title="Add Referee"
        description="Please choose the candidate & referee forms you'd like to send, and let us know how you'd prefer to provide the details for this additional referee."
        visible={visible}
        onClose={onClose}
        noPadding={inputType === 'ask_candidate'}
        className="u-min-width-small">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={
            inputType === 'ask_candidate'
              ? 'flex flex-col max-h-[min(85vh,calc(100vh-8rem))]'
              : undefined
          }>
          {inputType === 'ask_candidate' ? (
            <>
              <div className="overflow-y-auto flex-1 min-h-0 px-6 pt-2 pb-4">{formBody}</div>
              <div className="px-6 pb-6 pt-4 border-t border-neutral-4 shrink-0">
                {formActions(true)}
              </div>
            </>
          ) : (
            <>
              {formBody}
              {formActions(false)}
            </>
          )}
        </form>
      </AnimatedModal>
      <AnimatedModal
        className="u-width-small"
        visible={showNewUploadModal}
        title={t('common.uploadFile')}
        onClose={() => setShowNewUploadModal(false)}>
        <FilePicker
          value={upload}
          onChange={setUpload}
          accept={{ 'application/pdf': ['.pdf'], 'application/msword': ['.doc', '.docx'] }}
          useModel
          className="u-margin-y"
          bucketFolder={FilesFolders.CompanyDocuments}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
        />
        <Button className="u-width-100" onClick={setUploadedDocument}>
          Done
        </Button>
      </AnimatedModal>
    </>
  );
};
